<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  ACTIVITY_PRESET_MONTHS,
  todayIsoDate,
} from '../utils/offerFilterUtils';

const props = defineProps({
  showOfferType: {
    type: Boolean,
    default: false,
  },
  showPaymentKind: {
    type: Boolean,
    default: false,
  },
  showActivity: {
    type: Boolean,
    default: false,
  },
  showContractFilters: {
    type: Boolean,
    default: false,
  },
  showDocumentationFilters: {
    type: Boolean,
    default: false,
  },
  offerKindDraft: {
    type: String,
    default: 'all',
  },
  paymentKindDraft: {
    type: String,
    default: 'all',
  },
  activityDraft: {
    type: Object,
    default: () => ({ months: '', startDate: '', endDate: '' }),
  },
  contractPeriodDraft: {
    type: String,
    default: 'LAST_30_DAYS',
  },
  documentationPeriodDraft: {
    type: String,
    default: 'LAST_30_DAYS',
  },
  contractStatusDraft: {
    type: String,
    default: 'ALL',
  },
  activityFilterError: {
    type: String,
    default: '',
  },
  offerKindOptions: {
    type: Array,
    required: true,
  },
  paymentOptions: {
    type: Array,
    required: true,
  },
  contractPeriodOptions: {
    type: Array,
    default: () => [],
  },
  documentationPeriodOptions: {
    type: Array,
    default: () => [],
  },
  contractStatusOptions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'closeOfferType',
  'update:offerKindDraft',
  'clearOfferType',
  'applyOfferType',
  'closePaymentKind',
  'update:paymentKindDraft',
  'clearPaymentKind',
  'applyPaymentKind',
  'closeActivity',
  'update:activityDraft',
  'selectActivityPreset',
  'selectActivityCustomRange',
  'clearActivity',
  'applyActivity',
  'closeContractFilters',
  'update:contractPeriodDraft',
  'update:contractStatusDraft',
  'clearContractFilters',
  'applyContractFilters',
  'closeDocumentationFilters',
  'update:documentationPeriodDraft',
  'clearDocumentationFilters',
  'applyDocumentationFilters',
]);

const updateActivityDate = (field, value) => {
  emit('update:activityDraft', {
    ...props.activityDraft,
    [field]: value,
    months: '',
  });
  emit('selectActivityCustomRange');
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisDrawer
    :show="showOfferType"
    title="Tipo de proposta"
    side="bottom"
    size="sm"
    @close="emit('closeOfferType')"
  >
    <div class="flex flex-wrap gap-2">
      <button
        v-for="option in offerKindOptions"
        :key="option.value"
        type="button"
        class="min-h-9 rounded-full border px-3 text-[13px] font-semibold transition-colors"
        :class="
          offerKindDraft === option.value
            ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
            : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
        "
        @click="$emit('update:offerKindDraft', option.value)"
      >
        {{ option.label }}
      </button>
    </div>
    <template #footer>
      <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton variant="ghost" @click="emit('clearOfferType')">
          Limpar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-filter"
          @click="emit('applyOfferType')"
        >
          Aplicar filtro
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisDrawer>

  <LaisDrawer
    :show="showPaymentKind"
    title="Tipo de proposta"
    side="bottom"
    size="sm"
    @close="emit('closePaymentKind')"
  >
    <div class="grid gap-3">
      <label
        v-for="option in paymentOptions"
        :key="option.value"
        class="flex cursor-pointer items-center justify-between gap-3 rounded-lais border border-lais-border-soft px-3 py-3 transition-colors hover:border-lais-primary"
        :class="
          paymentKindDraft === option.value
            ? 'bg-lais-primary-50 border-lais-primary'
            : 'bg-white'
        "
      >
        <span class="font-semibold text-lais-text">{{ option.label }}</span>
        <input
          type="radio"
          name="offer-payment-kind"
          :value="option.value"
          :checked="paymentKindDraft === option.value"
          class="size-4 border-lais-border text-lais-primary focus:ring-lais-primary"
          @change="$emit('update:paymentKindDraft', option.value)"
        />
      </label>
    </div>
    <template #footer>
      <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton variant="ghost" @click="emit('clearPaymentKind')">
          Limpar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-filter"
          :disabled="!paymentKindDraft || paymentKindDraft === 'all'"
          @click="emit('applyPaymentKind')"
        >
          Aplicar filtro
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisDrawer>

  <LaisDrawer
    :show="showActivity"
    title="Tempo de atividade"
    side="bottom"
    size="md"
    @close="emit('closeActivity')"
  >
    <div class="grid gap-5">
      <section class="grid gap-3">
        <h3 class="text-[13px] font-semibold text-lais-text">
          Últimos períodos
        </h3>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="months in ACTIVITY_PRESET_MONTHS"
            :key="months"
            type="button"
            class="min-h-9 rounded-full border px-3 text-[13px] font-semibold transition-colors"
            :class="
              activityDraft.months === String(months)
                ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
            "
            @click="emit('selectActivityPreset', months)"
          >
            {{ months === 1 ? '1 mês' : `${months} meses` }}
          </button>
        </div>
      </section>

      <section class="grid gap-3">
        <h3 class="text-[13px] font-semibold text-lais-text">
          Período específico
        </h3>
        <div class="grid gap-3 sm:grid-cols-2">
          <label
            class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Data inicial
            <input
              type="date"
              :value="activityDraft.startDate"
              :max="activityDraft.endDate || todayIsoDate()"
              class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
              @input="updateActivityDate('startDate', $event.target.value)"
            />
          </label>
          <label
            class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Data final
            <input
              type="date"
              :value="activityDraft.endDate"
              :min="activityDraft.startDate"
              :max="todayIsoDate()"
              class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
              @input="updateActivityDate('endDate', $event.target.value)"
            />
          </label>
        </div>
        <p
          v-if="activityFilterError"
          class="rounded-lais-sm bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-700"
        >
          {{ activityFilterError }}
        </p>
      </section>
    </div>
    <template #footer>
      <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton variant="ghost" @click="emit('clearActivity')">
          Limpar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-filter"
          @click="emit('applyActivity')"
        >
          Aplicar filtro
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisDrawer>

  <LaisDrawer
    :show="showDocumentationFilters"
    title="Filtros de documentação"
    side="bottom"
    size="sm"
    @close="emit('closeDocumentationFilters')"
  >
    <section class="grid gap-3">
      <h3 class="text-[13px] font-semibold text-lais-text">
        Visualizar documentações:
      </h3>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="option in documentationPeriodOptions"
          :key="option.value"
          type="button"
          class="min-h-9 rounded-full border px-3 text-[13px] font-semibold transition-colors"
          :class="
            documentationPeriodDraft === option.value
              ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
              : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
          "
          @click="emit('update:documentationPeriodDraft', option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </section>
    <template #footer>
      <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton
          variant="ghost"
          @click="emit('clearDocumentationFilters')"
        >
          Limpar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-filter"
          @click="emit('applyDocumentationFilters')"
        >
          Aplicar filtro
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisDrawer>

  <LaisDrawer
    :show="showContractFilters"
    title="Filtros de contrato"
    side="bottom"
    size="md"
    @close="emit('closeContractFilters')"
  >
    <div class="grid gap-5">
      <section class="grid gap-3">
        <h3 class="text-[13px] font-semibold text-lais-text">Contratos em:</h3>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="option in contractPeriodOptions"
            :key="option.value"
            type="button"
            class="min-h-9 rounded-full border px-3 text-[13px] font-semibold transition-colors"
            :class="
              contractPeriodDraft === option.value
                ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
            "
            @click="emit('update:contractPeriodDraft', option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </section>

      <section class="grid gap-3">
        <h3 class="text-[13px] font-semibold text-lais-text">
          Visualizar contratos:
        </h3>
        <div class="grid gap-2 sm:grid-cols-2">
          <label
            v-for="option in contractStatusOptions"
            :key="option.value"
            class="flex cursor-pointer items-center justify-between gap-3 rounded-lais border border-lais-border-soft px-3 py-3 transition-colors hover:border-lais-primary"
            :class="
              contractStatusDraft === option.value
                ? 'border-lais-primary bg-lais-primary-50'
                : 'bg-white'
            "
          >
            <span class="font-semibold text-lais-text">{{ option.label }}</span>
            <input
              type="radio"
              name="rent-progress-contract-status"
              :value="option.value"
              :checked="contractStatusDraft === option.value"
              class="size-4 border-lais-border text-lais-primary focus:ring-lais-primary"
              @change="emit('update:contractStatusDraft', option.value)"
            />
          </label>
        </div>
      </section>
    </div>
    <template #footer>
      <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton
          variant="ghost"
          @click="emit('clearContractFilters')"
        >
          Limpar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-filter"
          @click="emit('applyContractFilters')"
        >
          Aplicar filtro
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisDrawer>
</template>
